// A sound's file heard for its beats (spec find-beats.md): the span asked for, read by ranges at the
// URL the server gave (with the media cookie for a file's own URL, as the clips' elements read it)
// and decoded by the browser's own decoder (WebCodecs, through mediabunny), made mono at about
// 11 kHz as each chunk comes, so a long file never sits whole in memory. Nothing is uploaded.
import { stoppableFetch } from "../../../api/download";
import { mediaCredentials } from "../../../api/media-cookie";
import { MonoSamples } from "./beats";

/** A sound heard: one channel at `rate`, from `fromMs` of its file. */
export interface HeardSound {
  samples: Float32Array;
  rate: number;
  fromMs: number;
}

/** A failed read is tried again twice, after 0.5 s and 1 s: a find the member waits for fails soon. */
function retryDelay(attempts: number, error: unknown): number | null {
  if (error instanceof DOMException && error.name === "AbortError") return null;
  return attempts < 3 ? 0.5 * 2 ** (attempts - 1) : null;
}

/**
 * The sound of the file at `url` from `fromMs` to `toMs` of it (or its end, if sooner), or null when
 * the file holds no sound; `onRead` hears how much of the span is read, 0 to 1. Stops, throwing,
 * once `signal` aborts.
 */
export async function hearSound(
  url: string,
  span: { fromMs: number; toMs: number },
  signal: AbortSignal,
  onRead: (part: number) => void,
): Promise<HeardSound | null> {
  const mb = await import("mediabunny");
  const source = new mb.UrlSource(url, {
    requestInit: { credentials: mediaCredentials(url) },
    fetchFn: stoppableFetch(() => signal),
    getRetryDelay: retryDelay,
    // A read stopped with the find is no error.
    handleUnhandledError: (error) => {
      if (!signal.aborted) throw error;
    },
  });
  const input = new mb.Input({ source, formats: mb.ALL_FORMATS });
  try {
    const track = await input.getPrimaryAudioTrack();
    if (track === null) return null;
    const endS = Math.min(await track.computeDuration(), span.toMs / 1000);
    const fromS = Math.min(Math.max(0, span.fromMs / 1000), endS);
    const mono = new MonoSamples(track.sampleRate, endS - fromS);
    for await (const chunk of new mb.AudioBufferSink(track).buffers(fromS, endS)) {
      signal.throwIfAborted();
      const { buffer } = chunk;
      const channels = Array.from({ length: buffer.numberOfChannels }, (_, at) => buffer.getChannelData(at));
      mono.add(channels, chunk.timestamp - fromS);
      onRead(Math.min(1, (chunk.timestamp + chunk.duration - fromS) / Math.max(0.001, endS - fromS)));
    }
    signal.throwIfAborted();
    return { samples: mono.read(), rate: mono.rate, fromMs: Math.round(fromS * 1000) };
  } finally {
    input.dispose();
  }
}
